import { Button, styled, useTheme } from '@mui/material';
import { ColumnFiltersState, Table } from '@tanstack/react-table';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { createSearchParams } from 'react-router-dom';
import { DatePicker } from '../../../components/DatePicker/DatePicker';
import { Multiselect } from '../../../components/Multiselect/Multiselect';
import { SingleSelect } from '../../../components/SingleSelect/SingleSelect';
import { StatusFilter } from '../../../components/StatusFilter/StatusFilter';
import { Typography } from '../../../components/Typography/Typography';
import {
  tsOpOverviewSelectedOPs,
  opOverviewStatusFilter,
  opOverviewSubmitterLabel,
} from '../../../state/UIAccountantState';
import { SelectItem, UserWithTsPeriodData } from '../../../types';
import { useFilteredOpsTimesheets } from '../../../queries/hooks/useFilteredOpsTimesheets';
import {
  ALL_SUBMITTERS_OPTION,
  useSubmitterLabelOptions,
} from '../../../queries/hooks/useSubmitterLabelOptions';

type Props = {
  columnFilters: ColumnFiltersState;
  table: Table<UserWithTsPeriodData>;
};

const useNavigateParams = () => {
  const navigate = useNavigate();

  return (pathname, params) => {
    const path = {
      pathname,
      search: createSearchParams(params).toString(),
    };
    navigate(path);
  };
};

export function FilterSection({ table, columnFilters }: Props) {
  const { colors } = useTheme();
  const location = useLocation();
  const navigateWithParams = useNavigateParams();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);

  const yearQueryParam = params.get('year');
  const currentYear = new Date().getFullYear();
  const year = yearQueryParam || String(currentYear);
  const startMonth = `${year}-01`;
  const endMonth = `${year}-12`;

  const [dateFilterValue, setDateFilterValue] = useState(yearQueryParam);
  const [selectedOPs, setSelectedOPs] = useAtom(tsOpOverviewSelectedOPs);
  const [statusValue, setStatusValue] = useAtom(opOverviewStatusFilter);
  const [submitterLabel, setSubmitterLabel] = useAtom(opOverviewSubmitterLabel);
  const submitterOptions = useSubmitterLabelOptions();

  const { data: operatingPartners } = useFilteredOpsTimesheets(
    startMonth,
    endMonth,
    statusValue,
    submitterLabel
  );

  const selectedSubmitterOption = useMemo(
    () => submitterOptions.find((option) => option.id === submitterLabel) ?? ALL_SUBMITTERS_OPTION,
    [submitterOptions, submitterLabel]
  );

  const onChangeSubmitterFilter = useCallback(
    (option: SelectItem | null) => {
      if (!option) return;
      setSubmitterLabel(String(option.id));
      setSelectedOPs([]);
      table.getColumn('name')?.setFilterValue([]);
    },
    [setSubmitterLabel, setSelectedOPs, table]
  );

  const sortedOPList = useMemo(
    () => {
      if (!operatingPartners) return [];
      const allOPs = operatingPartners.sort().map((user) => ({ id: user.id, value: user.name }));
      return allOPs;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table, operatingPartners]
  );

  const onChangeOPFilter = useCallback(
    (OPs: SelectItem[] | undefined) => {
      if (OPs) {
        setSelectedOPs(OPs);
        table.getColumn('name')?.setFilterValue(OPs);
      }
    },
    [table, setSelectedOPs]
  );

  const onChangeDateFilter = useCallback(
    (date: Date | null) => {
      if (date) {
        const year = date.getFullYear().toString();
        setDateFilterValue(year);
        navigateWithParams(location.pathname, { year });
      } else {
        setDateFilterValue('');
        navigate(location.pathname);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setDateFilterValue]
  );

  const onFiltersReset = useCallback(() => {
    setSelectedOPs([]);
    setSubmitterLabel('all');
    setDateFilterValue(null);
    setStatusValue('active');
    navigate(location.pathname);
  }, [location.pathname, navigate, setSelectedOPs, setSubmitterLabel, setStatusValue]);

  const isResetDisabled = useMemo(() => {
    const selectedOPs = columnFilters.find((filter) => filter.id === 'name')?.value as SelectItem[];
    if (selectedOPs?.length === 0 && !yearQueryParam && statusValue === 'active' && submitterLabel === 'all')
      return true;
    return false;
  }, [yearQueryParam, columnFilters, statusValue, submitterLabel]);

  useEffect(() => {
    table.getColumn('name')?.setFilterValue(selectedOPs);
  }, [selectedOPs, table]);

  return (
    <Container>
      <SingleSelect
        options={submitterOptions}
        value={selectedSubmitterOption}
        onChange={(_, option) => onChangeSubmitterFilter(option)}
        hideClearIcon
        style={{ width: '210px' }}
      />
      <Multiselect
        style={{ width: '200px', marginRight: '14px' }}
        fieldPlaceholder='Select submitter'
        options={sortedOPList as SelectItem[]}
        onChange={(_, OPs) => onChangeOPFilter(OPs)}
        value={selectedOPs}
      />

      <DatePicker
        clearable
        selectedDate={dateFilterValue}
        setSelectedDate={onChangeDateFilter}
        disableFuture
        views={['year']}
        inputFormat='yyyy'
        placeholder='Select year'
      />
      <StatusFilter value={statusValue} onChange={(value) => value && setStatusValue(value)} />
      <Button
        onClick={onFiltersReset}
        variant='text'
        style={{ height: '28px', marginTop: '2px' }}
        disabled={isResetDisabled}
        sx={{
          '.MuiButton-startIcon': { marginRight: '2px' },
          '&.Mui-disabled': { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'auto' },
        }}
      >
        <Typography
          variant='subtitle1'
          color={colors.textAccent.default}
        >
          Reset Filters
        </Typography>
      </Button>
    </Container>
  );
}

const Container = styled('div')`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  gap: 12px;
`;
